import { computed, defineComponent, nextTick, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { withLoading } from '@hems/component';
import { UserReportService } from '@hems/service';
import { getBatteryOptions } from '@hems/user/src/containers/report/battery/chartOptions';
import { divideArray, getEnergyBalance, getParam, getDateInitialValue, getNowByClient, } from '@hems/user/src/containers/report/chartFunctions';
import { useLocale } from '@hems/util';
import { getTime } from '@hems/util/src/helper/dateHelper';
import { GraphChart, EnergyBalance, CalendarHourly, CalendarDaily, CalendarMonth } from '@/components';
export default defineComponent({
    name: 'BatteryContainer',
    components: {
        GraphChart,
        EnergyBalance,
        CalendarHourly,
        CalendarDaily,
        CalendarMonth,
    },
    props: {
        tabname: {
            type: String,
            required: true,
            default: 'hourly',
        },
    },
    setup(props) {
        const { t } = useI18n();
        const store = useStore();
        const state = reactive({
            tabname: props.tabname,
            showChart: false,
            data: {
                charge: [],
                discharge: [],
                soc: [],
            },
            energyBalance: [],
            date: null,
        });
        const defaultBaseDeviceInfo = {
            device_id: '',
            site_id: 0,
            timezone_id: '',
            product_model_nm: '',
        };
        const { intlFormatLocale } = useLocale();
        const baseDeviceInfo = computed(() => store.state.device.baseDeviceInfo ?? null);
        const timezoneId = computed(() => store.state.appCtx.timeZone ?? '');
        const service = new UserReportService(window.axiosInstance.axios);
        const options = computed(() => getBatteryOptions(state.tabname, intlFormatLocale.value, state.data, t));
        async function getReportsByInterval(tabname) {
            await withLoading(async () => {
                if (state.date == null)
                    return;
                const date = state.date;
                const chartType = state.tabname === 'hourly' ? 'charge,discharge,soc' : 'charge,discharge';
                await service
                    .getReportsByInterval(tabname, getParam(date, chartType, tabname, baseDeviceInfo.value ?? defaultBaseDeviceInfo))
                    .then(({ result }) => {
                    state.energyBalance = divideArray([
                        { value: result.btc_kwh.origin || 0, unit: 'kWh', type: 'device.charged' },
                        { value: result.btd_kwh.origin || 0, unit: 'kWh', type: 'device.discharged' },
                    ], 2);
                    state.data.charge = result.colec_time.map((TIME, index) => ({
                        x: getTime(TIME, tabname),
                        y: result.btc_data[index] || 0,
                    }));
                    state.data.discharge = result.colec_time.map((TIME, index) => ({
                        x: getTime(TIME, tabname),
                        y: result.btd_data[index] || 0,
                    }));
                    if (state.tabname === 'hourly') {
                        state.data.soc = result.colec_time.map((TIME, index) => ({
                            x: getTime(TIME, tabname),
                            y: result.soc_data[index] || 0,
                        }));
                    }
                })
                    .catch((e) => {
                    console.error(e);
                    state.energyBalance = [[], [], [], [], []];
                    state.data.charge = [];
                    state.data.discharge = [];
                    state.data.soc = [];
                });
            })();
            nextTick(() => (state.showChart = true));
        }
        function rerenderDataGrid() {
            state.showChart = false;
            nextTick(() => (state.showChart = true));
        }
        function getServerTime() {
            service
                .getServerTime(baseDeviceInfo.value?.timezone_id ?? '')
                .then(async (res) => {
                state.date = getDateInitialValue(res);
            })
                .catch(async (e) => {
                console.error(e);
                state.date = getDateInitialValue(getNowByClient(timezoneId.value));
            });
        }
        watch(() => props.tabname, (tabname) => {
            state.tabname = tabname;
        });
        watch(() => state.tabname, () => {
            state.showChart = false;
            getReportsByInterval(state.tabname);
        });
        watch(() => state.date, () => {
            if (!state.date)
                return;
            getReportsByInterval(state.tabname);
        }, {
            deep: true,
        });
        watch(() => intlFormatLocale.value, () => {
            rerenderDataGrid();
        });
        getServerTime();
        return {
            state,
            options,
            energyBalance: computed(() => getEnergyBalance(state.energyBalance, intlFormatLocale.value, t)),
        };
    },
});
